import { z } from "zod";
import { containerPreferencesSchema } from "./preferences";
import { projectTechnologyHslColors } from "./project";

export const OVERVIEW_CONTAINER_LIMIT = 1;
export const NO_CONTAINER_LIMIT = 0;

export const containerIconNames = [
  "Map",
  "BatteryMedium",
  "Sprout",
  "Bubbles",
  "Earth",
  "Wind",
  "Droplets",
  "Sun",
  "Atom",
  "Factory",
  "Flame",
  "HardDrive",
] as const;
export type ContainerIconName = (typeof containerIconNames)[number];

export const containerColorNames = [
  "grey",
  "red",
  "orange",
  "amber",
  "yellow",
  "green",
  "teal",
  "cyan",
  "blue",
  "purple",
  "magenta",
  "pink",
] as const;
export type ContainerColor = (typeof containerColorNames)[number];

export const containerColors: Record<
  ContainerColor,
  { h: number; s: number; l: number; css: string }
> = {
  grey: { h: 0, s: 0, l: 45, css: "hsl(0, 0%, 45%)" },
  red: projectTechnologyHslColors.Battery,
  orange: projectTechnologyHslColors.Gas,
  amber: projectTechnologyHslColors["Solar Thermal"],
  yellow: projectTechnologyHslColors["Solar PV"],
  green: projectTechnologyHslColors["Onshore Wind"],
  teal: projectTechnologyHslColors.Biogas,
  cyan: projectTechnologyHslColors["Offshore Wind"],
  blue: projectTechnologyHslColors.Hydro,
  purple: projectTechnologyHslColors["Pumped Hydro"],
  magenta: projectTechnologyHslColors.Nuclear,
  pink: projectTechnologyHslColors["Data Centre"],
};

export const DEFAULT_CONTAINER_ICON: ContainerIconName = "Map";
export const DEFAULT_CONTAINER_COLOR: ContainerColor = "grey";

export const containerSchema = z.object({
  id: z.string().uuid(),
  creator_id: z.string(),
  name: z.string(),
  is_default: z.boolean(),
  icon: z.string(),
  color: z.string(),
  created: z.coerce.date(),
  updated: z.coerce.date(),
  preferences: containerPreferencesSchema,
});
export type Container = z.infer<typeof containerSchema>;

// db args
export const containerInsertArgsSchema = z.object({
  id: z.string().uuid(),
  creator_id: z.string(),
  name: z.string(),
  is_default: z.boolean(),
  icon: z.enum(containerIconNames),
  color: z.enum(containerColorNames),
  // existing container to delete while creating a new container
  // optional, enables a user to stay under a container limit in one transaction
  replace_id: z.string().uuid().optional(),
  // limit of 0 means unlimited containers
  limit: z.number(),
});
export type ContainerInsertArgs = z.infer<typeof containerInsertArgsSchema>;

export const containerDuplicateArgsSchema = z.object({
  source_id: z.string().uuid(),
  id: z.string().uuid(),
  creator_id: z.string(),
  limit: z.number(),
});
export type ContainerDuplicateArgs = z.infer<
  typeof containerDuplicateArgsSchema
>;

export const containerUpdateArgsSchema = z.object({
  id: z.string().uuid(),
  creator_id: z.string(),
  name: z.string(),
  is_default: z.boolean(),
  icon: z.enum(containerIconNames),
  color: z.enum(containerColorNames),
});
export type ContainerUpdateArgs = z.infer<typeof containerUpdateArgsSchema>;

// route args
export const containerPostSchema = z.object({
  id: z.string().uuid(),
  name: z.string().max(144),
  is_default: z.boolean(),
  icon: z.enum(containerIconNames),
  color: z.enum(containerColorNames),
  replace_id: z.string().uuid().optional(),
});
export type ContainerPostArgs = z.infer<typeof containerPostSchema>;

export const containerPatchSchema = z.object({
  name: z.string().max(144),
  is_default: z.boolean(),
  icon: z.enum(containerIconNames),
  color: z.enum(containerColorNames),
});
export type ContainerPatchArgs = z.infer<typeof containerPatchSchema>;
